import React, { useRef } from 'react'
import { useSearchStore } from '../../../store/jpeTopPageStore'
import { CurrencyPopover } from './CurrencyPopover'
import { HomeMenuPopover } from './HomeMenuPopover'

const CalendarIcon = () => {
  return (
    <svg
      className="w-5 h-5 sm:w-6 sm:h-6"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_28888_29189)">
        <path
          d="M9 11H7V13H9V11ZM13 11H11V13H13V11ZM17 11H15V13H17V11ZM19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20Z"
          fill="#6B7280"
        />
      </g>
      <defs>
        <clipPath id="clip0_28888_29189">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )
}

const UserIcon = () => {
  return (
    <svg
      className="w-5 h-5 sm:w-6 sm:h-6"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 12C10.9 12 9.95833 11.6083 9.175 10.825C8.39167 10.0417 8 9.1 8 8C8 6.9 8.39167 5.95833 9.175 5.175C9.95833 4.39167 10.9 4 12 4C13.1 4 14.0417 4.39167 14.825 5.175C15.6083 5.95833 16 6.9 16 8C16 9.1 15.6083 10.0417 14.825 10.825C14.0417 11.6083 13.1 12 12 12ZM4 20V17.2C4 16.6333 4.14583 16.1125 4.4375 15.6375C4.72917 15.1625 5.11667 14.8 5.6 14.55C6.63333 14.0333 7.68333 13.6458 8.75 13.3875C9.81667 13.1292 10.9 13 12 13C13.1 13 14.1833 13.1292 15.25 13.3875C16.3167 13.6458 17.3667 14.0333 18.4 14.55C18.8833 14.8 19.2708 15.1625 19.5625 15.6375C19.8542 16.1125 20 16.6333 20 17.2V20H4ZM6 18H18V17.2C18 17.0167 17.9542 16.85 17.8625 16.7C17.7708 16.55 17.65 16.4333 17.5 16.35C16.6 15.9 15.6917 15.5625 14.775 15.3375C13.8583 15.1125 12.9333 15 12 15C11.0667 15 10.1417 15.1125 9.225 15.3375C8.30833 15.5625 7.4 15.9 6.5 16.35C6.35 16.4333 6.22917 16.55 6.1375 16.7C6.04583 16.85 6 17.0167 6 17.2V18ZM12 10C12.55 10 13.0208 9.80417 13.4125 9.4125C13.8042 9.02083 14 8.55 14 8C14 7.45 13.8042 6.97917 13.4125 6.5875C13.0208 6.19583 12.55 6 12 6C11.45 6 10.9792 6.19583 10.5875 6.5875C10.1958 6.97917 10 7.45 10 8C10 8.55 10.1958 9.02083 10.5875 9.4125C10.9792 9.80417 11.45 10 12 10Z"
        fill="#8B9096"
      />
    </svg>
  )
}

interface JpeSearchBarProps {
  useAbsolutePosition?: boolean // Controls whether to use absolute positioning (default: true)
  showCurrency?: boolean // Controls whether to show currency selector (default: true)
  showMenu?: boolean // Controls whether to show menu button (default: true)
  customClassName?: string // Optional custom class name to add to the container
}

export const JpeSearchBar: React.FC<JpeSearchBarProps> = ({
  useAbsolutePosition = true,
  showCurrency = true,
  showMenu = true,
  customClassName = '',
}) => {
  const {
    searchData,
    setModalOpen,
    selectedCurrency,
    isCurrencyPopoverOpen,
    setCurrencyPopoverOpen,
    isHomeMenuPopoverOpen,
    setHomeMenuPopoverOpen,
  } = useSearchStore()
  const currencyButtonRef = useRef<HTMLButtonElement>(null)
  const homeMenuButtonRef = useRef<HTMLButtonElement>(null)

  const handleOpenModal = () => {
    setModalOpen(true)
  }

  const handleToggleCurrencyPopover = () => {
    setCurrencyPopoverOpen(!isCurrencyPopoverOpen)
  }

  const handleCloseCurrencyPopover = () => {
    setCurrencyPopoverOpen(false)
  }

  const handleToggleHomeMenuPopover = () => {
    setHomeMenuPopoverOpen(!isHomeMenuPopoverOpen)
  }

  const handleCloseHomeMenuPopover = () => {
    setHomeMenuPopoverOpen(false)
  }

  // Display function for dates in search bar
  const renderDateDisplay = () => {
    if (searchData.plan_month.length === 0) {
      return ''
    }

    const sortedPlanMonth = [...searchData.plan_month].sort((a, b) => {
      const [yearA, monthA] = a.split('/')
      const [yearB, monthB] = b.split('/')
      if (yearA === yearB) {
        return parseInt(monthA) - parseInt(monthB)
      } else {
        return parseInt(yearA) - parseInt(yearB)
      }
    })
    return sortedPlanMonth.join(' ~ ')
  }

  // Display function for guest counts in search bar
  const renderGuestDisplay = () => {
    const { number_of_adults, number_of_children, number_of_preschoolers_1, number_of_students_1 } = searchData

    const parts: string[] = []

    if (number_of_adults > 0) {
      parts.push(`${number_of_adults} adult${number_of_adults > 1 ? 's' : ''}`)
    }

    if (number_of_children > 0) {
      parts.push(`${number_of_children} ${number_of_children > 1 ? 'children' : 'child'}`)
    }

    if (number_of_preschoolers_1 > 0) {
      parts.push(`${number_of_preschoolers_1} preschooler${number_of_preschoolers_1 > 1 ? 's' : ''}`)
    }

    if (number_of_students_1 > 0) {
      parts.push(`${number_of_students_1} student${number_of_students_1 > 1 ? 's' : ''}`)
    }

    return parts.join(', ')
  }

  // Check if search is empty (no filters applied)
  const isSearchEmpty = () => {
    return (
      searchData.plan_month.length === 0 &&
      searchData.number_of_adults === 0 &&
      searchData.number_of_children === 0 &&
      searchData.number_of_preschoolers_1 === 0 &&
      searchData.number_of_students_1 === 0
    )
  }

  const baseClassName = useAbsolutePosition
    ? 'absolute top-0 left-0 right-0 px-2 sm:px-4 pt-3 sm:pt-4 pb-2 sm:pb-3 z-10'
    : 'w-full'
  const containerClassName = customClassName ? `${baseClassName} ${customClassName}` : baseClassName

  return (
    <div className={containerClassName}>
      <div className="flex bg-white items-center rounded-lg shadow-md transition-shadow">
        {isSearchEmpty() ? (
          // Empty state - show default placeholder
          <>
            <div
              onClick={handleOpenModal}
              className={`flex-1 ${
                !useAbsolutePosition ? 'px-2 py-2' : 'px-2 sm:px-4 py-2 sm:py-3'
              }  cursor-pointer transition-colors`}
            >
              <span className="text-xs sm:text-sm text-gray-600">Search by Age & Duration</span>
            </div>

            {showCurrency && (
              <>
                <div className="h-6 sm:h-8 w-px bg-gray-200" />

                <div className="relative ml-auto">
                  <button
                    ref={currencyButtonRef}
                    onClick={handleToggleCurrencyPopover}
                    className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm hover:bg-gray-50 transition-colors"
                  >
                    {selectedCurrency}
                    <svg
                      className="w-2 h-2 sm:w-2 sm:h-2"
                      width="8"
                      height="4"
                      viewBox="0 0 8 4"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M0 0L3.75 3.75L7.5 0H0Z" fill="#111827" />
                    </svg>
                  </button>

                  <CurrencyPopover
                    isOpen={isCurrencyPopoverOpen}
                    onClose={handleCloseCurrencyPopover}
                    buttonRef={currencyButtonRef}
                  />
                </div>
              </>
            )}

            {showMenu && (
              <>
                <div className="h-6 sm:h-8 w-px bg-gray-200" />

                <div className="relative">
                  <button
                    ref={homeMenuButtonRef}
                    onClick={handleToggleHomeMenuPopover}
                    className="px-2 sm:px-4 py-2 sm:py-3 bg-white hover:bg-gray-50 transition-colors rounded-r-lg"
                  >
                    <svg
                      className="w-4 h-3 sm:w-[18px] sm:h-3"
                      width="18"
                      height="12"
                      viewBox="0 0 18 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M0 12H18V10H0V12ZM0 7H18V5H0V7ZM0 0V2H18V0H0Z" fill="#323232" />
                    </svg>
                  </button>

                  <HomeMenuPopover
                    isOpen={isHomeMenuPopoverOpen}
                    onClose={handleCloseHomeMenuPopover}
                    buttonRef={homeMenuButtonRef}
                  />
                </div>
              </>
            )}
          </>
        ) : (
          // Has filters - show icon layout with values
          <>
            <div
              className="flex items-center truncate"
              style={{
                maxWidth: '65%',
                minWidth: '60%',
              }}
            >
              {renderDateDisplay() && (
                <>
                  <div
                    onClick={handleOpenModal}
                    className="flex items-center gap-1 px-1 sm:px-2 py-2 sm:py-3 cursor-pointer hover:bg-gray-50 transition-colors flex-1 min-w-0 truncate"
                    style={{
                      flex: 1,
                    }}
                  >
                    <div className="shrink-0">
                      <CalendarIcon />
                    </div>
                    <span className="text-xs sm:text-sm text-gray-900 truncate whitespace-nowrap">{renderDateDisplay()}</span>
                  </div>

                  <div className="h-6 sm:h-8 w-px bg-gray-200" />
                </>
              )}
              {renderGuestDisplay() && (
                <div
                  onClick={handleOpenModal}
                  className="flex items-center gap-1 sm:gap-2 px-1 sm:px-2 py-2 sm:py-3 cursor-pointer hover:bg-gray-50 transition-colors min-w-0 truncate"
                  style={{ flex: 1 }}
                >
                  <div className="shrink-0">
                    <UserIcon />
                  </div>
                  <span className="text-xs sm:text-sm text-gray-900 truncate whitespace-nowrap">{renderGuestDisplay()}</span>
                </div>
              )}
            </div>

            {showCurrency && (
              <>
                <div className="h-6 sm:h-8 w-px bg-gray-200" />

                <div className="relative ml-auto">
                  <button
                    ref={currencyButtonRef}
                    onClick={handleToggleCurrencyPopover}
                    className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 sm:py-3 text-xs sm:text-sm hover:bg-gray-50 transition-colors"
                  >
                    {selectedCurrency}
                    <svg
                      className="w-2 h-2 sm:w-2 sm:h-2"
                      width="8"
                      height="4"
                      viewBox="0 0 8 4"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M0 0L3.75 3.75L7.5 0H0Z" fill="#111827" />
                    </svg>
                  </button>

                  <CurrencyPopover
                    isOpen={isCurrencyPopoverOpen}
                    onClose={handleCloseCurrencyPopover}
                    buttonRef={currencyButtonRef}
                  />
                </div>
              </>
            )}

            {showMenu && (
              <>
                <div className="h-6 sm:h-8 w-px bg-gray-200" />

                <div className="relative">
                  <button
                    ref={homeMenuButtonRef}
                    onClick={handleToggleHomeMenuPopover}
                    className="px-2 sm:px-4 py-2 sm:py-3 bg-white hover:bg-gray-50 transition-colors rounded-r-lg"
                  >
                    <svg
                      className="w-4 h-3 sm:w-[18px] sm:h-3"
                      width="18"
                      height="12"
                      viewBox="0 0 18 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M0 12H18V10H0V12ZM0 7H18V5H0V7ZM0 0V2H18V0H0Z" fill="#323232" />
                    </svg>
                  </button>

                  <HomeMenuPopover
                    isOpen={isHomeMenuPopoverOpen}
                    onClose={handleCloseHomeMenuPopover}
                    buttonRef={homeMenuButtonRef}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
